import { Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ElementType } from 'src/app/_objects/element-type';
import { ReportMeasurementPoint } from 'src/app/_objects/report-measurement-point';
import { FromToDetail } from 'src/app/_objects/report-template';
import { LangUpdateService } from 'src/app/_services/lang-update.service';
import { MousePoint, Notifier, ReportTemplateDesignService } from 'src/app/_services/report-template-design.service';

@Component({
    selector: 'app-report-measurement-points',
    templateUrl: './report-measurement-points.component.html',
    styleUrls: ['./report-measurement-points.component.scss'],
    standalone: false
})
export class ReportMeasurementPointComponent implements OnInit {
  @Input() public pageKey: string;
  @Input() public measurementPointKey: string;
  @Input() public elementLocation: string;

  public notifier: Notifier;
  public reportMeasurementPoint: ReportMeasurementPoint;
  public resizeMarkerSize: number;
  public resizing: boolean;
  constructor(
    public designService: ReportTemplateDesignService,
    private translate: TranslateService,
    private langService: LangUpdateService
) {
    this.langService.prepare(this, this.translate, (res) => {
      });
    this.resizeMarkerSize = 36;
    this.resizing = false;
  }

  public ngOnInit() {
    this.notifier = {
      componentName: 'report-measurement-points.component',
      ids: { pageId: this.pageKey, elementId: this.measurementPointKey }
    };
    this.reportMeasurementPoint = this.designService.getMeasurementPoints(this.measurementPointKey);
    let elementNavigator = this.designService.navigatorElements
    .filter((x) => x.pageKey == this.pageKey)[0]
    .elementsDetails.filter((y) => y.location == this.elementLocation)[0]
      .elements;

     elementNavigator.filter(x=>x.elementName == ElementType.MeasurementPoint).length > 0
      ? elementNavigator.filter(x=>x.elementName == ElementType.MeasurementPoint)[0].elementData.push({
          id: this.measurementPointKey,
          page: this.pageKey,
          name: this.reportMeasurementPoint.getTitle(),
        })
      : elementNavigator.push({
          elementName: ElementType.MeasurementPoint,
          isVisible: false,
          elementData: [
            {
              id: this.measurementPointKey,
              page: this.pageKey,
              name: this.reportMeasurementPoint.getTitle(),
            },
          ],
        });
  }

  public onClick(event: MouseEvent) {
    if (this.reportMeasurementPoint.isSelectable) {
      this.designService.notifySelectElement(this.notifier, this.measurementPointKey, event.ctrlKey);
      event.stopPropagation();
    }
  }

  public onDragStart(event: DragEvent) {
    // console.log('ReportImage onDragStart');
    event.dataTransfer.setData('measurementPoint', this.measurementPointKey);
    event.dataTransfer.effectAllowed = 'move';

    const fromDetail: FromToDetail = {
      location: this.reportMeasurementPoint.getLocation(),
      pageId: this.pageKey
    };
    const dragPoint: MousePoint = {
      onElementX: event.offsetX,
      onElementY: event.offsetY
    };

    this.designService.notifyBeginDrag(this.notifier, ElementType.MeasurementPoint, fromDetail, dragPoint);
  }

  public onDragOver(event: DragEvent) {
    // console.log('ReportImage onDragOver');
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    event.stopPropagation();
  }

  public onDrop(event: DragEvent) {
    // console.log('ReportImage onDrop');
    event.preventDefault();
    const template  = {...this.designService.originTemplate};
    template.reportTemplateContent = JSON.stringify(this.designService.getTemplate());
    this.designService.templateForUndo.push(template);
    const dragElemId = event.dataTransfer.getData('measurementPoint');

    const toDetail: FromToDetail = {
      location: this.reportMeasurementPoint.getLocation(),
      pageId: this.pageKey
    };
    const dropPoint: MousePoint = {
      onElementX: this.reportMeasurementPoint.getLeftPx() + event.offsetX,
      onElementY: this.reportMeasurementPoint.getTopPx() + event.offsetY
    };

    this.designService.notifyDrop(this.notifier, dragElemId, toDetail, dropPoint);

    event.dataTransfer.clearData('measurementPoint');
    event.stopPropagation();
  }

  public getStyles(reportMeasurementPoint: ReportMeasurementPoint): object {
    return {
      width: reportMeasurementPoint.tableWidth,
      fontSize: reportMeasurementPoint.fontSize,
      fontFamily: reportMeasurementPoint.fontFamily,
      border: `${reportMeasurementPoint.tableBorderWidth}mm ${reportMeasurementPoint.tableBorderStyle} ${reportMeasurementPoint.tableBorderColor}`,
      padding: `${reportMeasurementPoint.tableMargin}mm`
    };
  }

  public getContainerStyle(reportMeasurementPoint: ReportMeasurementPoint): object {
    return {
      top: 0,
      left: 0,
      width: reportMeasurementPoint.width,
      height: reportMeasurementPoint.height
    };
  }

  public getResizeMarkerSize(): string {
    return this.resizeMarkerSize + 'px';
  }

  public getResizeMarkerLeft(horizontal: string): string {
    switch (horizontal) {
      case 'left':
        return (this.resizeMarkerSize / 2 * -1) + 'px';
      case 'center':
        return ((this.reportMeasurementPoint.getWidthPx() / 2) - (this.resizeMarkerSize / 2)) + 'px';
      case 'right':
        return (this.reportMeasurementPoint.getWidthPx() - (this.resizeMarkerSize / 2)) + 'px';
      default:
        return '';
    }
  }

  public getResizeMarkerTop(vertical: string): string {
    switch (vertical) {
      case 'top':
        return (this.resizeMarkerSize / 2 * -1) + 'px';
      case 'middle':
        return ((this.reportMeasurementPoint.getHeightPx() / 2) - (this.resizeMarkerSize / 2)) + 'px';
      case 'bottom':
        return (this.reportMeasurementPoint.getHeightPx() - (this.resizeMarkerSize / 2)) + 'px';
      default:
        return '';
    }
  }

  public onResizeMarkerDown(event: MouseEvent, vertical: string, horizontal: string) {
    event.preventDefault();
    this.resizing = true;
    const beginPoint: MousePoint = {
      onElementX: event.offsetX,
      onElementY: event.offsetY
    };
    this.designService.notifyBeginResize(this.notifier, ElementType.Text, horizontal, vertical, beginPoint);

    event.stopPropagation();
  }

  public onResizing(event: MouseEvent) {
    event.preventDefault();
    if (this.resizing) {
      this.designService.notifyResize(this.notifier, { onElementX: event.offsetX, onElementY: event.offsetY });
    }
    event.stopPropagation();
  }

  public onResizeMarkerUp(event: MouseEvent) {
    event.preventDefault();
    this.resizing = false;
    event.stopPropagation();
  }

  public onResizeMarkerLeave(event: MouseEvent) {
    event.preventDefault();
    this.resizing = false;
    event.stopPropagation();
  }

}
